import { inject, Injectable, Provider } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { QUESTION_EVENTS_SERVICE_TOKEN } from '../../services/IQuestionEvents';

/**
 * Hides manipulative bottom control bars for every manipulative rendered beneath the providing component.
 * Nested scopes inherit a hidden state from any ancestor scope.
 */
@Injectable()
export class ManipControlBarScope {
  private readonly parent = inject(ManipControlBarScope, { optional: true, skipSelf: true });
  private readonly hiddenInScope = new BehaviorSubject(false);

  public readonly hidden$: Observable<boolean> = (this.parent
    ? combineLatest([this.parent.hidden$, this.hiddenInScope]).pipe(map(([parentHidden, hidden]) => parentHidden || hidden))
    : this.hiddenInScope.asObservable()
  ).pipe(distinctUntilChanged());

  public get hidden(): boolean {
    return this.hiddenInScope.value || (this.parent?.hidden ?? false);
  }

  public setHidden(hidden: boolean): void {
    this.hiddenInScope.next(hidden);
  }
}

export function provideHiddenManipControlBars(): Provider {
  return {
    provide: ManipControlBarScope,
    useFactory: (): ManipControlBarScope => {
      const scope = new ManipControlBarScope();
      scope.setHidden(true);
      return scope;
    }
  };
}

export type ManipControlBarVisibility = {
  hidden$: Observable<boolean>;
  isHidden: () => boolean;
};

/**
 * Must be called in an injection context. Combines the app-wide `hideStudentManipControlBar` flag
 * with the nearest `ManipControlBarScope`, so the control bar and its hints share one source of truth.
 */
export function injectManipControlBarVisibility(): ManipControlBarVisibility {
  const appWideHidden = inject(QUESTION_EVENTS_SERVICE_TOKEN).hideStudentManipControlBar;
  const scope = inject(ManipControlBarScope, { optional: true });

  const hidden$ = scope
    ? combineLatest([appWideHidden, scope.hidden$]).pipe(map(([appHidden, scopeHidden]) => appHidden || scopeHidden))
    : appWideHidden.asObservable();

  return {
    hidden$: hidden$.pipe(distinctUntilChanged()),
    isHidden: () => appWideHidden.value || (scope?.hidden ?? false)
  };
}
